// ============================================================================
// Tenant / company-employee model.
//
// All business data (invoices, clients, products, …) is keyed to the account
// OWNER's id. A business owner operates on their own id; an invited team member
// (manager, accountant, employee, comercial) operates on their EMPLOYER's id —
// so they see and work on the company's shared resources, not an empty silo.
//
// Use getOwnerId() to scope business-data reads/writes (the `user_id` column).
// Use the real auth user id only for "who did this" fields (audit, created_by)
// and for editing the person's OWN profile.
//
// The database RLS mirrors this: `user_id = auth.uid() OR user_id = get_employer_id()`.
// ============================================================================
import { supabase } from './supabase';

let _cache = null; // { uid, ownerId, employerId, role }

// Drop the cached tenant (call on logout / when the active user changes).
export function clearTenantCache() {
  _cache = null;
}

// Resolve the current user plus the account owner they operate under.
// Returns { user, ownerId, employerId, role, isTeamMember }.
export async function getTenant() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { user: null, ownerId: null, employerId: null, role: null, isTeamMember: false };
  }
  if (_cache && _cache.uid === user.id) {
    return { user, ownerId: _cache.ownerId, employerId: _cache.employerId, role: _cache.role, isTeamMember: !!_cache.employerId };
  }
  const { data: profile } = await supabase
    .from('profiles')
    .select('employer_id, role')
    .eq('id', user.id)
    .single();
  const employerId = profile?.employer_id || null;
  const ownerId = employerId || user.id;
  _cache = { uid: user.id, ownerId, employerId, role: profile?.role || null };
  return { user, ownerId, employerId, role: profile?.role || null, isTeamMember: !!employerId };
}

// Convenience: the account owner id to scope business data by.
export async function getOwnerId() {
  return (await getTenant()).ownerId;
}
